import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Card, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavBar from "app/layout/TopNavBar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CalendarServices from "app/services/CalendarServices";
import LHHCApiService from "app/services/LHHCApiService";
import countries from "components/LH/HC/DateTimePlaceEntry/data/countries";
import LocationSearchTextBox from "components/LH/HC/LocationSearchTextBox";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/si";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const years = Array.from({ length: 151 }, (_, index) => 1900 + index).map((year) => ({
  id: year,
  label: String(year),
}));

const months = monthNames.map((name, index) => ({ Id: index, Name: name }));

const fieldSx = {
  backgroundColor: "#ffffff",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: 1,
    height: 44,
    boxSizing: "border-box",
    fontSize: "0.95rem",
    "& fieldset": {
      borderColor: "#d0d0d0",
    },
    "&:hover fieldset": {
      borderColor: "#aeaeae",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
      borderWidth: 1.5,
    },
  },
  "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
  },
  "& .MuiInputBase-input": {
    color: "#333333",
    padding: "10px 12px",
  },
};

const labelSx = { mb: 0.5, fontWeight: 700, color: "#555555", fontSize: "0.85rem" };

const formatCalendarEntryLabel = (option, t) => {
  if (!option) return "";
  moment.locale("si");
  const startTime = (option.startTime || "").replace("-", ":");
  const endTime = (option.endTime || "").replace("-", ":");
  const dateLabel = moment(option.date, "YYYY-MM-DD").format("D dddd");
  const startLabel = moment(`${option.date} ${startTime}`, "YYYY-MM-DD HH:mm").format("a h:mm");
  const endLabel = moment(`${option.date} ${endTime}`, "YYYY-MM-DD HH:mm").format("a h:mm");
  return `${dateLabel}, ${startLabel} ${t("shubaMuhurthaSearch.fromLabel")} ${endLabel} ${t("shubaMuhurthaSearch.untilLabel")} ${option.text}.`;
};

const initialFormState = {
  country: countries[0],
  year: years.find((year) => year.id === new Date().getFullYear()) || years[0],
  month: months[new Date().getMonth()],
  event: null,
  eventDate: null,
  location: null,
  nekaths: [null, null, null],
};

const getDirection = (value, positive, negative) => (value >= 0 ? positive : negative);

const getStatusSx = (shuba) => ({
  display: "inline-block",
  ml: 0.75,
  px: 0.75,
  py: 0.2,
  borderRadius: 1,
  fontSize: "0.7rem",
  fontWeight: 700,
  color: shuba ? "#2e7d32" : "#c62828",
  backgroundColor: shuba ? "#e8f5e9" : "#ffebee",
});

function ShubaMuhurthaSearch() {
  const { t } = useTranslation();
  const { getEvents } = CalendarServices();
  const { searchMuhurtha, getMuhurthaEvents } = LHHCApiService();
  const [form, setForm] = useState(initialFormState);
  const [muhurthaEvents, setMuhurthaEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [selectedPanchangaya, setSelectedPanchangaya] = useState([]);
  const [selectedMuhurthaTimes, setSelectedMuhurthaTimes] = useState([]);
  const [muhurthaView, setMuhurthaView] = useState("all");
  const [selectionLimitOpen, setSelectionLimitOpen] = useState(false);

  const nekathOptions = useMemo(
    () => Array.from({ length: 27 }, (_, index) => ({ id: `Nekatha${index + 1}`, label: t(`Nekatha${index + 1}`) })),
    [t]
  );

  const selectedMonthKey = useMemo(() => {
    if (!form.year || !form.month) return null;
    return `${form.year.id}-${String(form.month.Id + 1).padStart(2, "0")}`;
  }, [form.year, form.month]);

  const allMuhurthaTimes = searchResult?.muhurthaTimes || [];
  const displayedMuhurthaTimes = muhurthaView === "selected"
    ? allMuhurthaTimes.filter((item) => selectedMuhurthaTimes.includes(item.muhuthaTime))
    : allMuhurthaTimes;

  const toggleMuhurthaTime = (muhuthaTime) => {
    if (!selectedMuhurthaTimes.includes(muhuthaTime) && selectedMuhurthaTimes.length >= 3) {
      setSelectionLimitOpen(true);
      return;
    }

    setSelectedMuhurthaTimes((current) => (
      current.includes(muhuthaTime)
        ? current.filter((value) => value !== muhuthaTime)
        : [...current, muhuthaTime]
    ));
  };

  useEffect(() => {
    const fetchMuhurthaEvents = async () => {
      try {
        setEventsLoading(true);
        setEventsError("");
        const data = await getMuhurthaEvents();
        setMuhurthaEvents(data);
        setForm((current) => ({ ...current, event: data[0] || null }));
      } catch (requestError) {
        setMuhurthaEvents([]);
        setEventsError(requestError.response?.data?.message || "Unable to load Muhurtha events.");
      } finally {
        setEventsLoading(false);
      }
    };

    fetchMuhurthaEvents();
  }, [getMuhurthaEvents]);

  useEffect(() => {
    const fetchMonthEntries = async () => {
      if (!selectedMonthKey) {
        setCalendarEntries([]);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const data = await getEvents(selectedMonthKey);
        setCalendarEntries(Array.isArray(data) ? data : []);
      } catch (requestError) {
        setCalendarEntries([]);
        setError(requestError.response?.data?.message || "Unable to load month entries.");
      } finally {
        setLoading(false);
      }
    };

    fetchMonthEntries();
  }, [getEvents, selectedMonthKey]);

  const updateNekath = (index, value) => {
    setForm((current) => {
      const next = [...current.nekaths];
      next[index] = value;
      if (value === null) {
        for (let i = index + 1; i < next.length; i += 1) {
          next[i] = null;
        }
      }
      return { ...current, nekaths: next };
    });
  };

  const isMissing = (value) => formError && !value;
  const requiredLabelSx = (value) => ({ ...labelSx, color: isMissing(value) ? "#d32f2f" : labelSx.color });

  const handleSearch = async () => {
    setFormError(true);
    const isValid =
      form.country && form.location && form.year && form.month && form.nekaths[0] && form.event && form.eventDate;
    if (isValid) {
      alert("Form is valid!");
      setFormError(false);
      const location = form.location;
      const latitude = location.locationGeoData.lat;
      const longitude = location.locationGeoData.lng;
      const dateTimeFrom = `${form.eventDate.date} ${form.eventDate.startTime}`;
      const dateTimeTo = `${form.eventDate.date} ${form.eventDate.endTime}`;
      const request = {
        SubId: localStorage.getItem("UseActiveAccount") || "",
        Year: form.year.id,
        Month: form.month.Id + 1,
        DateTimeSearchFrom: dateTimeFrom,
        DateTimeSearchTo: dateTimeTo,
        CountryCode: form.country.code,
        CountryName: form.country.label,
        TownName: location.location.label,
        Longitude: longitude,
        LongitudeDecimal: longitude,
        LongitudeDirection: getDirection(longitude, "E", "W"),
        Latitude: latitude,
        LatitudeDecimal: latitude,
        LatitudeDirection: getDirection(latitude, "N", "S"),
        Location: location.location.label,
        DateTimeLocalString: moment(`${form.eventDate.date} ${(form.eventDate.startTime || "").replace("-", ":")}`, "YYYY-MM-DD HH:mm").format("LLLL"),
        Name: form.event.name,
        PlaceId: location.location.place_id,
        NekathBaseOn: form.nekaths.filter(Boolean).map((nekath) => nekath.id),
        EventId: form.event.id,
      };

      try {
        setSearchLoading(true);
        setSearchError("");
        setSearchResult(null);
        setSelectedMuhurthaTimes([]);
        setMuhurthaView("all");
        setSearchResult(await searchMuhurtha(request));
      } catch (requestError) {
        setSearchError(requestError.response?.data?.message || "Unable to search Shuba Muhurtha times.");
      } finally {
        setSearchLoading(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <TopNavBar />
      <MDBox
        pt={3}
        pb={4}
        sx={{
          px: { xs: 2, md: 3 },
          minHeight: "100vh",
          width: "100%",
          m: -3,
        }}
      >
        <Card
          sx={{
            maxWidth: "100%",
            mx: "auto",
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ borderBottom: "1px solid #eeeeee", py: 1 }}>
            <MDTypography variant="button" sx={{ display: "block", textAlign: "center", color: "#8a8a8a", fontSize: "0.8rem" }}>
              {t("shubaMuhurthaSearch.navTitle")}
            </MDTypography>
          </Box>

          <MDTypography
            variant="h5"
            fontWeight="bold"
            sx={{ textAlign: "center", color: "#1e1e1e", mt: 2.5, mb: 1 }}
          >
            {t("shubaMuhurthaSearch.searchTitle")}
          </MDTypography>

          <Divider sx={{ mx: 3 }} />

          <Box sx={{ maxWidth: "900px", mx: "auto", p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={2.5}>
              
               <Grid item size={{ xs: 12 }}>
                <Grid container spacing={2.5} justifyContent="center">
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDTypography variant="caption" sx={requiredLabelSx(form.event)}>
                      {t("shubaMuhurthaSearch.selectEvent")}
                    </MDTypography>
                    <Autocomplete
                      options={muhurthaEvents}
                      value={form.event}
                      getOptionLabel={(option) => (option ? option.name : "")}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      onChange={(_, value) => setForm((current) => ({ ...current, event: value }))}
                      disabled={eventsLoading || muhurthaEvents.length === 0}
                      loading={eventsLoading}
                      noOptionsText={eventsError || t("shubaMuhurthaSearch.noMuhurthaEvents")}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: "block" }}>
                          <MDTypography variant="button" fontWeight="bold">{option.name}</MDTypography>
                          <MDTypography variant="caption" sx={{ display: "block", color: "#757575" }}>{option.description}</MDTypography>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          fullWidth
                          sx={fieldSx}
                          error={isMissing(form.event)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: <>{eventsLoading ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>,
                          }}
                        />
                      )}
                    />
                    {eventsError && <MDTypography variant="caption" sx={{ display: "block", color: "#d32f2f", mt: 0.5 }}>{eventsError}</MDTypography>}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <Grid container spacing={2.5} justifyContent="center">
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDTypography variant="caption" sx={requiredLabelSx(form.nekaths[0])}>
                      {t("shubaMuhurthaSearch.selectNekath")}
                    </MDTypography>
                    <Autocomplete
                      options={nekathOptions}
                      value={form.nekaths[0] || null}
                      onChange={(_, value) => updateNekath(0, value)}
                      getOptionLabel={(option) => (option ? option.label : "")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          fullWidth
                          sx={fieldSx}
                          label={t("shubaMuhurthaSearch.nekathLabel")}
                          error={isMissing(form.nekaths[0])}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item size={{ xs: 12, md: 6 }}>
                <MDTypography variant="caption" sx={requiredLabelSx(form.country)}>
                  {t("COUNTRY") || "Country"}
                </MDTypography>
                <Autocomplete
                  options={countries}
                  value={form.country}
                  onChange={(_, value) => setForm((current) => ({ ...current, country: value || countries[0] }))}
                  getOptionLabel={(option) => option.label || ""}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        loading="lazy"
                        src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
                        alt=""
                        style={{ width: 24, height: 18 }}
                      />
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      fullWidth
                      sx={{
                        ...fieldSx,
                        "& .MuiOutlinedInput-root": {
                          ...fieldSx["& .MuiOutlinedInput-root"],
                          height: "44px !important",
                        },
                        "& .MuiOutlinedInput-input": {
                          height: "44px !important",
                          boxSizing: "border-box",
                          padding: "0 12px !important",
                        },
                      }}
                      error={isMissing(form.country)}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: form.country?.code ? (
                          <Box component="span" sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                            <img
                              src={`https://flagcdn.com/24x18/${form.country.code.toLowerCase()}.png`}
                              alt=""
                              style={{ width: 22, height: 16, marginRight: 8 }}
                            />
                          </Box>
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <MDTypography variant="caption" sx={requiredLabelSx(form.location)}>
                  {t("LOCATION") || "Location"}
                </MDTypography>
                <Box
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      height: "44px !important",
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "44px !important",
                      boxSizing: "border-box",
                      padding: "0 12px !important",
                    },
                  }}
                >
                  <LocationSearchTextBox
                    countryCode={form.country?.code}
                    date={`${form.year?.id || new Date().getFullYear()}-${String((form.month?.Id ?? 0) + 1).padStart(2, "0")}-01`}
                    onLocationChnage={(locationData) =>
                      {
                      console.log("locationData========", locationData);
                      setForm((current) => ({ ...current, location: locationData }))
                      }
                    }
                    error={isMissing(form.location)}
                  />
                </Box>
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <MDTypography variant="caption" sx={requiredLabelSx(form.year)}>
                  {t("YEAR") || "Year"}
                </MDTypography>
                <Autocomplete
                  options={years}
                  value={form.year}
                  getOptionLabel={(option) => option.label}
                  onChange={(_, value) => setForm((current) => ({ ...current, year: value || years[0] }))}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" fullWidth sx={fieldSx} error={isMissing(form.year)} />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <MDTypography variant="caption" sx={requiredLabelSx(form.month)}>
                  {t("MONTH") || "Month"}
                </MDTypography>
                <Autocomplete
                  options={months}
                  value={form.month}
                  getOptionLabel={(option) => option.Name}
                  onChange={(_, value) => setForm((current) => ({ ...current, month: value || months[new Date().getMonth()] }))}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" fullWidth sx={fieldSx} error={isMissing(form.month)} />
                  )}
                />
              </Grid>

             



              <Grid item size={{ xs: 12 }}>
                <Grid container spacing={2.5} justifyContent="center">
                  <Grid item size={{ xs: 12, md: 9 }}>
                    <MDTypography variant="caption" sx={requiredLabelSx(form.eventDate)}>
                      {t("shubaMuhurthaSearch.calendarEntry")}
                    </MDTypography>
                    <Autocomplete
                      options={calendarEntries}
                      value={form.eventDate}
                      getOptionLabel={(option) => formatCalendarEntryLabel(option, t)}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      onChange={(_, value) => setForm((current) => ({ ...current, eventDate: value }))}
                      disabled={loading || calendarEntries.length === 0}
                      noOptionsText={loading ? t("shubaMuhurthaSearch.loadingEntries") : t("shubaMuhurthaSearch.noEntries")}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" fullWidth sx={fieldSx} error={isMissing(form.eventDate)} />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {formError && (
              <MDTypography variant="caption" sx={{ display: "block", textAlign: "center", color: "#d32f2f", mt: 2 }}>
                {t("shubaMuhurthaSearch.requiredFieldsError")}
              </MDTypography>
            )}

            <MDBox mt={2} display="flex" justifyContent="center">
              <MDButton
                variant="contained"
                color="info"
                size="large"
                onClick={handleSearch}
                sx={{
                  minWidth: 280,
                  minHeight: 48,
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "uppercase",
                  boxShadow: "none",
                }}
              >
                {t("shubaMuhurthaSearch.searchButton")}
              </MDButton>
            </MDBox>

            {searchLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {searchError && (
              <MDTypography variant="caption" sx={{ display: "block", textAlign: "center", color: "#d32f2f", mt: 2 }}>
                {searchError}
              </MDTypography>
            )}

            {searchResult && !searchLoading && (
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1e1e1e" }}>
                    {t("shubaMuhurthaSearch.muhurthaTimes")}
                  </MDTypography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={muhurthaView}
                    onChange={(_, value) => value && setMuhurthaView(value)}
                    aria-label={t("shubaMuhurthaSearch.muhurthaTimes")}
                  >
                    <ToggleButton value="all">{t("shubaMuhurthaSearch.allResults", { count: allMuhurthaTimes.length })}</ToggleButton>
                    <ToggleButton value="selected">{t("shubaMuhurthaSearch.selectedResults", { count: selectedMuhurthaTimes.length })}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {muhurthaView === "selected" && displayedMuhurthaTimes.length === 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 3 }}>
                    <MDTypography variant="caption" sx={{ textAlign: "center", color: "#757575" }}>
                      {t("shubaMuhurthaSearch.noSelectedMuhurthaTimes")}
                    </MDTypography>
                    <MDButton variant="outlined" color="info" size="small" onClick={() => setMuhurthaView("all")}>
                      {t("shubaMuhurthaSearch.viewAllResults")}
                    </MDButton>
                  </Box>
                ) : (
                <TableContainer sx={{ maxHeight: muhurthaView === "all" ? 440 : "none", overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Table size="small" sx={{ width: "100%", minWidth: 600 }}>
                    <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0, backgroundColor: "#f7f7f7", position: "sticky", top: 0, zIndex: 1 }}>
                      <TableRow>
                        <TableCell sx={{ width: 48 }} />
                        <TableCell>{t("shubaMuhurthaSearch.muhuthaTime")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.horawa")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.panchamaHorawa")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.sukshamaHorawa")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.panchangaya")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedMuhurthaTimes.map((item) => {
                        const isSelected = selectedMuhurthaTimes.includes(item.muhuthaTime);
                        return (
                        <TableRow key={item.muhuthaTime} hover sx={{ backgroundColor: isSelected ? "#e3f2fd" : "inherit" }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleMuhurthaTime(item.muhuthaTime)}
                              inputProps={{ "aria-label": `${t("shubaMuhurthaSearch.selectMuhurthaTime")} ${item.muhuthaTime}` }}
                            />
                          </TableCell>
                          <TableCell>{item.muhuthaTime}</TableCell>
                          <TableCell>{item.horawa?.value}<Box component="span" sx={getStatusSx(item.horawa?.shuba)}>{item.horawa?.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}</Box></TableCell>
                          <TableCell>{item.panchamaHorawa?.value}<Box component="span" sx={getStatusSx(item.panchamaHorawa?.shuba)}>{item.panchamaHorawa?.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}</Box></TableCell>
                          <TableCell>{item.sukshamaHorawa?.value}<Box component="span" sx={getStatusSx(item.sukshamaHorawa?.shuba)}>{item.sukshamaHorawa?.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}</Box></TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                              {item.panchangaya?.map((entry) => (
                                <Tooltip
                                  key={entry.type}
                                  title={`${entry.type} | ${entry.value} | ${entry.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}`}
                                >
                                  <IconButton
                                    aria-label={`${entry.name}: ${entry.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}`}
                                    size="small"
                                    onClick={() => setSelectedPanchangaya(item.panchangaya || [])}
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      p: 0,
                                      color: entry.shuba ? "#2e7d32" : "#ed8b00",
                                    }}
                                  >
                                    <TaskAltOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}

                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1e1e1e", mt: 3, mb: 1.5 }}>
                  {t("shubaMuhurthaSearch.rahuKalaya")}
                </MDTypography>
                <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Table size="small" sx={{ width: "100%", minWidth: 680, tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0, backgroundColor: "#f7f7f7" }}>
                      <TableRow>
                        <TableCell sx={{ width: "42%" }}>{t("shubaMuhurthaSearch.date")}</TableCell>
                        <TableCell sx={{ width: "14.5%" }}>{t("shubaMuhurthaSearch.dayStart")}</TableCell>
                        <TableCell sx={{ width: "14.5%" }}>{t("shubaMuhurthaSearch.dayEnd")}</TableCell>
                        <TableCell sx={{ width: "14.5%" }}>{t("shubaMuhurthaSearch.nightStart")}</TableCell>
                        <TableCell sx={{ width: "14.5%" }}>{t("shubaMuhurthaSearch.nightEnd")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResult.rahuKalaya?.map((item) => (
                        <TableRow key={item.date} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{moment(item.date, "YYYY-MM-DD").format("YYYY-MM-DD dddd")}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{item.rahuKalayaDayTimeStartTime}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{item.rahuKalayaDayTimeEndTime}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{item.rahuKalayaNightTimeStartTime}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{item.rahuKalayaNightTimeEndTime}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Dialog open={selectedPanchangaya.length > 0} onClose={() => setSelectedPanchangaya([])} fullWidth maxWidth="sm">
              <DialogTitle>{t("shubaMuhurthaSearch.panchangayaDetails")}</DialogTitle>
              <DialogContent dividers>
                <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Table size="small" sx={{ minWidth: 460 }}>
                    <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0, backgroundColor: "#f7f7f7" }}>
                      <TableRow>
                        <TableCell>{t("shubaMuhurthaSearch.type")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.value")}</TableCell>
                        <TableCell>{t("shubaMuhurthaSearch.status")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPanchangaya.map((entry, index) => (
                        <TableRow key={`${entry.type}-${index}`}>
                          <TableCell>{entry.type}</TableCell>
                          <TableCell>{entry.value}</TableCell>
                          <TableCell><Box component="span" sx={{ ...getStatusSx(entry.shuba), ml: 0 }}>{entry.shuba ? t("shubaMuhurthaSearch.shuba") : t("shubaMuhurthaSearch.notShuba")}</Box></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DialogContent>
              <DialogActions>
                <MDButton color="info" onClick={() => setSelectedPanchangaya([])}>{t("shubaMuhurthaSearch.close")}</MDButton>
              </DialogActions>
            </Dialog>

            <Dialog open={selectionLimitOpen} onClose={() => setSelectionLimitOpen(false)} fullWidth maxWidth="xs">
              <DialogTitle>{t("shubaMuhurthaSearch.selectionLimitTitle")}</DialogTitle>
              <DialogContent dividers>
                <MDTypography variant="caption">{t("shubaMuhurthaSearch.selectionLimitMessage")}</MDTypography>
              </DialogContent>
              <DialogActions>
                <MDButton color="info" onClick={() => setSelectionLimitOpen(false)}>{t("shubaMuhurthaSearch.close")}</MDButton>
              </DialogActions>
            </Dialog>
          </Box>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default ShubaMuhurthaSearch;
